import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useParams, useHistory, useRouteMatch } from "react-router-dom"
import { Button, Form, Row, Col, notification, Upload, Table, Checkbox, Dropdown, Menu, Skeleton } from "antd"
import { LeftOutlined, FileAddOutlined, DownloadOutlined, LoadingOutlined, DownOutlined, FolderOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons"
import nodePath from "path"

import client from "../../lib/client"
import { selectUid } from "../../stores/appSlice"
import {
    billsImportUrl,
    ledgerAccountsImportTemplateUrl,
    ledgerAccountsImportUrl,
    ledgerFieldsImportUrl,
    vendorsImportUrl,
    divisionImportUrl,
    facilityImportUrl,
    billableAccountImportUrl,
    supplyAccountImportUrl,
    ledgerFieldUrl,
    arcadiaApiUrl,
    billsArcadiaImportUrl,
    clientVendorNumberImportUrl,
} from "../../lib/endpoints"
import divisionsTemplate from "../../assets/import_templates/divisions_import_template.csv"
import facilitiesTemplate from "../../assets/import_templates/facilities_import_template.csv"
import accountsImportTemplate from "../../assets/import_templates/accounts_import_template.csv"
import supplyAccountsImportTemplate from "../../assets/import_templates/supply_accounts_import_template.csv"
import vendorsTemplate from "../../assets/import_templates/vendors_import_template.csv"
import ledgerFieldsTemplate from "../../assets/import_templates/ledger_fields_import_template.csv"
import billsTemplate from "../../assets/import_templates/bills_import_template.csv"
import billsDescription from "../../assets/import_descriptions/bills_import_description.txt"
import billsTemplateExample from "../../assets/import_templates/bills_import_template_example.csv"
import clientVendorNumberTemplateExample from "../../assets/import_templates/client_vendor_number_import_template.csv"
import moment from "moment"
import Role from "../userRoles/Role"
import { createArcadiaImport, deleteImportBills, fetchAlaiBillFile, fetchArcadiaInfo, selectArcadiaInfo, selectLoading, selectTotal, setArcadiaInfo } from "./importsSlice"
import { useCheckIfImportExportAllowed } from "../../lib/utilities"

export default function ImportForm({ dataType }) {
    const { url } = useRouteMatch()
    const history = useHistory()
    const { clientId } = useParams()
    const dispatch = useDispatch()
    const uid = useSelector(selectUid)

    const [file, setFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    // const [loading, setLoading] = useState(false)
    const [bills, setBills] = useState([])
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0,  })
    const [checkedBills, setCheckedBills] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [openingBillId, setOpeningBillId] = useState(null)
    
    const arcadiaInfo = useSelector(selectArcadiaInfo)
    const total = useSelector(selectTotal)
    const loading = useSelector(selectLoading)

    // console.log(arcadiaInfo)

    // if(!useCheckIfImportExportAllowed()) history.goBack()
        
    useEffect(()=>{
        dispatch(setArcadiaInfo({ arcadiaInfo: [], total: 0 })) // Make sure table is cleared when switching between clients
    },[])

    const fileSelect = (file) => {
        setFile(file)
        return false
    }

    const fileRemove = () => {
        setFile(null)
    }

    const openNotification = () => {
        notification.open({
            message: "Starting Import Process",
            description: `Starting ${dataType} import process`,
            duration: 2,
        })
    }

    const getEndpointForDataType = (dataType) => {
        switch (dataType) {
            case "bills":
                return `${billsImportUrl(clientId)}?connection_id=${uid}`
            case "vendor":
                return `${vendorsImportUrl}?connection_id=${uid}`
            case "ledger_account":
                return `${ledgerAccountsImportUrl(clientId)}?connection_id=${uid}` 
            case "ledger_field":
                return `${ledgerFieldsImportUrl(clientId)}?connection_id=${uid}`    
            case "division":
                return `${divisionImportUrl(clientId)}?connection_id=${uid}`
            case "facility":
                return `${facilityImportUrl(clientId)}?connection_id=${uid}`
            case "billableAccount":
                return `${billableAccountImportUrl(clientId)}?connection_id=${uid}`
            case "supplyAccount":
                return `${supplyAccountImportUrl(clientId)}?connection_id=${uid}`
            case "clientVendorNumber":
                return `${clientVendorNumberImportUrl(clientId)}?connection_id=${uid}`
            default:
                return divisionImportUrl()
        }
    }

    const getImportTemplate = (dataType) => {
        switch (dataType) {
            case "ledger_account":
               return (window.location.href = ledgerAccountsImportTemplateUrl(clientId))
            case "bills":
                window.location.href = billsTemplate
                break   
            case "ledger_field":
                window.location.href = ledgerFieldsTemplate
                break
            case "vendor":
                window.location.href = vendorsTemplate
                break
            case "facility":
                window.location.href = facilitiesTemplate
                break
            case "division":
                window.location.href = divisionsTemplate
                break
            case "billableAccount":
                return (window.location.href = accountsImportTemplate)
            case "supplyAccount":
                return (window.location.href = supplyAccountsImportTemplate)
            case "clientVendorNumber":
                window.location.href = clientVendorNumberTemplateExample
                break
            default:
                return divisionsTemplate
        }
    }

    const getFormattedDataType = (dataType) => {
        switch (dataType) {
            case "ledger_account":
                return  "Ledger Accounts"
            case "bills":
                return  "Bills"    
            case "ledger_field":
                return  "Ledger Fields"
            case "vendor":
                return "Vendors"
            case "division":
                return "Divisions"
            case "facility":
                return "Facilities"
            case "billableAccount":
                return "Billable Accounts"
            case "supplyAccount":
                return "Supply Accounts"
            case "clientVendorNumber":
                return "Client Vendor Numbers"
            default:
                return "Divisions"
        }
    }

    const startImport = async (options) => {
        openNotification()
        setUploading(true)
        const fmData = new FormData()
        fmData.append("file", file)

        try {
            const response = await client({
                method: "post",
                url: getEndpointForDataType(dataType),
                data: fmData,
                config: {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                },
            })
        } catch (err) {
            console.log("Error: ", err)
        }
        setUploading(false)
    }

    // TODO: Move to other file
    const cols = [
        "Client",
        "Account Number",
        "Bill Date",
        "Due Date",
        "Balance Forward",
        "Current Charges",
        "Late Fee",
        "Amount Due",
        "Invoice Number",
        "Notes",
        "Rebill/Adjustment", 
        "QC",
        "Parked",
        "Attachment",
        "Meter Number 1",
        "Previous Read Date 1",
        "Read Date 1",
        "Previous Reading 1",
        "Meter Reading 1",
        "Multiplier 1",
        "Usage 1",
        "Unit 1",
        "Estimated 1",
        "Utility Charges 1",
        "Utility Taxes 1",
        "Supply Charges 1",
        "Supply Taxes 1",
        "Other Charge 1",
        "Rec Charge 1",
        "Therm Factor 1",
        "Adjustment Factor 1",
        "Demand 1",
        "KW Actual 1",
        "KW Billed 1",
        "Power Factor 1",
        "Meter Number 2",
        "Previous Read Date 2",
        "Read Date 2",
        "Previous Reading 2",
        "Meter Reading 2",
        "Multiplier 2",
        "Usage 2",
        "Unit 2",
        "Estimated 2",
        "Utility Charges 2",
        "Utility Taxes 2",
        "Supply Charges 2",
        "Supply Taxes 2",
        "Other Charge 2",
        "Rec Charge 2",
        "Therm Factor 2",
        "Adjustment Factor 2",
        "Demand 2",
        "KW Actual 2",
        "KW Billed 2",
        "Power Factor 2",
        "Meter Number 3",
        "Previous Read Date 3",
        "Read Date 3",
        "Previous Reading 3",
        "Meter Reading 3",
        "Multiplier 3",
        "Usage 3",
        "Unit 3",
        "Estimated 3",
        "Utility Charges 3",
        "Utility Taxes 3",
        "Supply Charges 3",
        "Supply Taxes 3",
        "Other Charge 3",
        "Rec Charge 3",
        "Therm Factor 3",
        "Adjustment Factor 3",
        "Demand 3",
        "KW Actual 3",
        "KW Billed 3",
        "Power Factor 3",
    ]

    const handleClick = async (action = 'fetch') => {
        switch(action){
            case 'fetch':
                // console.log('here')
                await dispatch(fetchArcadiaInfo('Bills', clientId))
                setLoaded(true)
                return
            case 'create':
                const response = await dispatch(createArcadiaImport(billsArcadiaImportUrl(clientId), checkedBills)) 
                setLoaded(true)
                if(response != 'error'){
                    dispatch(setArcadiaInfo([]))
                    history.push(`/clients/${clientId}/bills`)
                }
                else{
                    setCheckedBills([])
                }
                return
            case 'reload':
                await dispatch(fetchArcadiaInfo('Bills', clientId))
                setLoaded(true)
                return
            default:
                notification.error({
                    duration: 2,
                    message: "Error",
                    description: "An error occurred."
                })

        }
    }

    function TableCol(col){
        // const newCol = camelCaseString(col)
        const newCol = col.toLowerCase().replaceAll(" ", "_")
        const visibleColumns = [
            "Account Number",
            "Bill Date",
            "Due Date",
            "Balance Forward",
            "Current Charges",
            "Late Fee",
            "Amount Due",
            "Invoice Number",
            "Meter Number 1",
            "Previous Read Date 1",
            "Read Date 1",
            "Previous Reading 1",
            "Meter Reading 1",
            "Multiplier 1",
            "Usage 1",
            "Unit 1",
            "Utility Charges 1",
            "Notes"
            // "Demand 1",
            // "KW Actual 1",
            // "KW Billed 1",
        ]

        if(visibleColumns.includes(col)){
            return (
                <Table.Column
                    fixed={false}
                    title={col}
                    key={newCol}
                    showSorterTooltip={false}
                    dataIndex={newCol}
                    render={(text, record, index) => {
                        if (text) {
                            if (newCol.includes("date")) return moment(text).format("L")
                            if (typeof text == 'number') return text.toLocaleString()

                            return text
                        } else {
                            return text
                        }
                    }}
                />
            )
        }
    }

    function checkForBill(id, returnIndex = false){
        for(let i = 0; i < checkedBills.length; i++){
            if (id == checkedBills[i].id) {
                if (returnIndex) {
                    return { result: true, index: i }
                }
                return true
            }
        }
        return false
    }

    const openBillFile = async (id) => {
        // Open the tab synchronously so popup blockers allow it, then point it at the PDF once fetched
        const win = window.open("", "_blank")
        setOpeningBillId(id)
        try {
            const blob = await fetchAlaiBillFile(id)
            const fileUrl = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }))
            win.location.href = fileUrl
            setTimeout(() => window.URL.revokeObjectURL(fileUrl), 60000)
        } catch (e) {
            if (win) win.close()
            notification.error({
                duration: 10,
                message: "Error",
                description: "Unable to load the bill PDF.",
            })
        }
        setOpeningBillId(null)
    }

    function deleteBill(key){
        dispatch(setArcadiaInfo(arcadiaInfo.filter((bill, i) => bill.key !== key)))
    }

    function deleteBills(){
        let deletedCount = 0
        const alaiBills = []
        const ids = checkedBills.map((bill, i) => {
            if(!bill.id.includes("stm_")) {
                alaiBills.push({ bill_id: bill.id, message: "This bill was deleted by the user", status: "ignore" })
            }
            return bill.id
        })

        if(alaiBills.length > 0){
            dispatch(deleteImportBills(alaiBills))
        }

        const newBills = arcadiaInfo.filter((bill, i) => !ids.includes(bill.id))
        dispatch(setArcadiaInfo(newBills))       
        setCheckedBills([])
    }

    const handleTableChange = (info) => {
        setPagination(info)
        // console.log(info)
    }

    function checkIfAllSelected(){
        return checkedBills.length == arcadiaInfo.length
    }

    return (
        <div id="user-form-container">
            <Link to={nodePath.join(url, "../").replace(/\/$/, "")}>
                <LeftOutlined />
                Go Back
            </Link>

            <div className="card">
                <div className="card-body">
                    <div>
                        <Row gutter={24}>
                            <Col span={24}>
                                <h3>
                                    Import {getFormattedDataType(dataType)}
                                    {dataType === "bills" ? " using CSV file" : ""}
                                </h3>
                                <p>Import larger quantities of data all at once using CSV Import.</p>
                                <p>
                                    <b>
                                        The required format of the file data is VERY specific. It is HIGHLY recommended you download the template file below to ensure your data is properly imported.
                                    </b>
                                </p>
                                <p>Import could potentially be successful with incorrect data. Please make sure to check all the bills created for any issues.</p>
                                {dataType === "bills" ? (
                                    <>
                                        <p>PDF's are not added automatically</p>
                                        <ul>
                                            <li>
                                                <Button
                                                    icon={<DownloadOutlined />}
                                                    onClick={() => {
                                                        const link = document.createElement("a")
                                                        link.href = billsDescription // Make sure this is a valid URL
                                                        link.download = "Formatting_Import_CSV_Guide.txt"
                                                        document.body.appendChild(link)
                                                        link.click()
                                                        document.body.removeChild(link)
                                                    }}
                                                    style={{ marginBottom: "20px" }}>
                                                    Download Guide
                                                </Button>
                                                <p> - A small guide on what exactly is needed in each column</p>
                                            </li>
                                            <li>
                                                <Button icon={<DownloadOutlined />} onClick={() => getImportTemplate(dataType)} style={{ marginBottom: "20px" }}>
                                                    Download Template File
                                                </Button>
                                                <p> - Template to use when uploading file.</p>
                                            </li>
                                            <li>
                                                <Button icon={<DownloadOutlined />} onClick={() => (window.location.href = billsTemplateExample)} style={{ marginBottom: "20px" }}>
                                                    Download Example File
                                                </Button>
                                                <p> - An example file with a few rows of fake data showing the formatting.</p>
                                            </li>
                                        </ul>
                                    </>
                                ) : (
                                    <Button icon={<DownloadOutlined />} onClick={() => getImportTemplate(dataType)} style={{ marginBottom: "20px" }}>
                                        Download Template File
                                    </Button>
                                )}
                            </Col>
                        </Row>

                        <hr />

                        <Row gutter={24}>
                            <Col span={24}>
                                <Upload maxCount={1} accept="csv" beforeUpload={fileSelect} onRemove={fileRemove}>
                                    <Button icon={<FileAddOutlined />}>Select Import File</Button>
                                </Upload>
                            </Col>
                        </Row>

                        <br />

                        <Row gutter={24}>
                            <Col span={24}>
                                <Button type="primary" onClick={() => startImport()} loading={uploading}>
                                    Submit
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>

            {/* Probably move this to a new page */}
            {dataType === "bills" && (
                <div className="card" style={{ marginTop: "2%" }}>
                    <div className="card-body">
                        <div>
                            <Row gutter={24}>
                                <Col span={24}>
                                    <h3>Import 2</h3>
                                    <p>Fetch all the bills with their required fields</p>
                                    <p>
                                        <b>Please make sure all the bills are correct!</b>
                                    </p>
                                </Col>
                            </Row>

                            <hr />

                            {loaded && (
                                <>
                                    <div className="card-body-heading">
                                        <div className="badged-header">
                                            <h2>Total bills found: </h2>
                                            <div className="badge">{total}</div>
                                        </div>
                                        <div className="actions">
                                            <div className="table-action-links">
                                                {checkedBills.length > 0 && (
                                                    <a href="#" onClick={() => deleteBills()}>
                                                        Delete Bill{checkedBills.length > 1 ? "s" : ""}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <Skeleton active loading={loading}>
                                        <Table
                                            dataSource={arcadiaInfo}
                                            pagination={{ ...pagination, showSizeChanger: true, total: total, pageSizeOptions: [10, 20, 50, 100, 1000] }}
                                            onChange={handleTableChange}
                                            scroll={{ x: "max-content" }}
                                            rowKey={(r) => r.id}>
                                            {/* <Table.Column
                                                title={<Checkbox onChange={(e) => console.log(e)} />}
                                                key="in"
                                                dataIndex="in"
                                                render={(text, record) => (
                                                    <Checkbox
                                                        defaultChecked={true}
                                                        checked={false}
                                                        onChange={(e) => console.log(e)}
                                                        disabled={false}
                                                    />
                                                )}
                                            /> */}
                                            <Table.Column
                                                key="in"
                                                dataIndex="in"
                                                title={
                                                    <Checkbox
                                                        checked={checkedBills.length == arcadiaInfo.length}
                                                        onChange={(e) => {
                                                            for (let i = 0; i < arcadiaInfo.length; i++) {
                                                                const bill = arcadiaInfo[i]
                                                                const billCheck = checkForBill(bill.id, true)
                                                                if (e.target.checked) {
                                                                    if (!billCheck) {
                                                                        checkedBills.push(bill)
                                                                    }
                                                                } else {
                                                                    if (billCheck) {
                                                                        checkedBills.splice(billCheck.index, 1)
                                                                    }
                                                                }
                                                            }
                                                            setCheckedBills([...checkedBills])
                                                        }}
                                                    />
                                                }
                                                render={(text, record, index) => {
                                                    return (
                                                        <Checkbox
                                                            defaultChecked={false}
                                                            checked={checkForBill(record.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setCheckedBills(checkedBills.concat(record))
                                                                } else {
                                                                    setCheckedBills(checkedBills.filter((bill, i) => bill.id !== record.id))
                                                                }
                                                            }}
                                                            // disabled={paymentBatch.closed}
                                                        />
                                                    )
                                                }}
                                            />
                                            {cols.map((col, i) => TableCol(col))}
                                            <Table.Column
                                                fixed="right"
                                                title="Bill Link"
                                                key="bill_link"
                                                render={(text, record) =>
                                                    record.id && !String(record.id).includes("stm_") ? (
                                                        <a
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault()
                                                                openBillFile(record.id)
                                                            }}>
                                                            {openingBillId === record.id ? <LoadingOutlined /> : "View PDF"}
                                                        </a>
                                                    ) : null
                                                }
                                            />
                                            {/* <Table.Column
                                                fixed={"right"}
                                                title=""
                                                key="action"
                                                render={(text, record) => (
                                                    <>
                                                        <Dropdown
                                                            overlay={
                                                                <Menu>
                                                                    <Role action="destroy" model="bill">
                                                                        <Menu.Item key="4" icon={<DeleteOutlined />} onClick={() => deleteBill(record.key)}>
                                                                            <Link to={`#`}>Remove</Link>
                                                                        </Menu.Item>
                                                                    </Role>
                                                                </Menu>
                                                            }>
                                                            <Button>
                                                                Actions <DownOutlined />
                                                            </Button>
                                                        </Dropdown>
                                                    </>
                                                )}
                                            /> */}
                                        </Table>
                                    </Skeleton>
                                </>
                            )}
                            <br />

                            <Row gutter={24}>
                                <Col span={!loaded ? 24 : null}>
                                    <Button
                                        type="primary"
                                        onClick={() => handleClick(!loaded ? "fetch" : "create")}
                                        loading={loading}
                                        disabled={loaded && ((arcadiaInfo.length == 0 && !loading ? true : false) || checkedBills.length == 0)}>
                                        {loading ? "Loading" : !loaded ? "Fetch Bills" : `Create ${checkedBills.length} Bill${checkedBills.length == 1 ? "" : "s"}`}
                                    </Button>
                                </Col>
                                {loaded && !loading && (
                                    <Col>
                                        <Button type="primary" onClick={() => handleClick("reload")} loading={loading}>
                                            <ReloadOutlined />
                                            {loading ? "Loading" : "Reload Bills"}
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

